import { type } from 'arktype';

// =============================================================================
// Entity Audit Change Models
// =============================================================================

/**
 * Audit change entry for a specific entity record.
 * Includes old/new data snapshots for diff display.
 */
export const entityChangeSchema = type({
  id: type('number').configure({
    description: 'Monotonic audit log entry ID.',
    examples: [104231],
  }),
  schemaName: type('string').configure({
    description: 'Database schema that owns the audited table.',
    examples: ['core', 'planning'],
  }),
  tableName: type('string').configure({
    description: 'Table name of the audited record.',
    examples: ['persons'],
  }),
  recordId: type('string').configure({
    description: 'Primary-key value of the audited record.',
    examples: ['per_abc123'],
  }),
  operation: type("'INSERT' | 'UPDATE' | 'DELETE'").configure({
    description: "Type of change: 'INSERT' created a record, 'UPDATE' modified one, 'DELETE' removed one.",
  }),
  'changedBy?': type('string | null').configure({
    description: 'User ID that performed the change. Optional and nullable for system-generated changes.',
    examples: ['usr_abc123'],
  }),
  'changedByName?': type('string | null').configure({
    description: 'Human-readable name of the user that performed the change. Optional and nullable.',
    examples: ['Anna Müller'],
  }),
  changedAt: type('string').configure({
    description: 'ISO 8601 timestamp of when the change was applied.',
    examples: ['2026-04-22T08:15:30.000Z'],
  }),
  'oldData?': type('Record<string, unknown> | null').configure({
    description: 'Snapshot of the record before the change. Optional and nullable (null for INSERT).',
    examples: [{ firstName: 'Anna', lastName: 'Meier' }],
  }),
  'newData?': type('Record<string, unknown> | null').configure({
    description: 'Snapshot of the record after the change. Optional and nullable (null for DELETE).',
    examples: [{ firstName: 'Anna', lastName: 'Müller' }],
  }),
}).configure({ description: 'Audit log entry describing a single change to an entity record.' });

export type EntityChange = typeof entityChangeSchema.t;

/**
 * Paginated response for entity audit changes
 */
export const entityChangesResponseSchema = type({
  data: entityChangeSchema.array(),
  total: type('number').configure({
    description: 'Total number of audit entries matching the query (ignores paging).',
    examples: [42],
  }),
}).configure({ description: 'Paginated response envelope for entity audit changes.' });

export type EntityChangesResponse = typeof entityChangesResponseSchema.t;
